<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.general',
    path: 'general',
    component: 'GeneralSettings',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'GeneralSettings',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const sendmailResult = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    sendmailResult: sendmailResult.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'sendmailResult' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      sendmailResult,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_972461zw3_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1269646916756998_setData_ov9sdieuy_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'sendmailResult',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1269646916756998_setData_rt0por968_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'sendmailResult',
      value: true,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1269646916756998_request_pd21mcms9_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'post_sendmail_test',
      data: {},
    };
  };

  const act_7315092382398562_closeModal_972461zw3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_972461zw3_getActionArgs,
    });
  const inst_evh_7315092382398562_close_7vqm22lt0 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_972461zw3,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_7vqm22lt0],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_7yn6clsfr =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_qzgkkduj2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7yn6clsfr,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_qzgkkduj2],
    event: 'reload',
  });
  const act_1269646916756998_setData_ov9sdieuy = new actionTypes.SetDataAction({
    actionArgs: act_1269646916756998_setData_ov9sdieuy_getActionArgs,
  });
  const inst_evh_1269646916756998_clickPreventStop_6qgv5kmhe =
    new actionTypes.EventHandlerImpl({
      action: act_1269646916756998_setData_ov9sdieuy,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const act_1269646916756998_setData_rt0por968 = new actionTypes.SetDataAction({
    actionArgs: act_1269646916756998_setData_rt0por968_getActionArgs,
  });
  const inst_evh_1269646916756998_success_5db8uwfeg =
    new actionTypes.EventHandlerImpl({
      action: act_1269646916756998_setData_rt0por968,
      event: 'success',
      displayName: 'setData',
    });
  const act_1269646916756998_request_pd21mcms9 = new actionTypes.RequestAction({
    actionArgs: act_1269646916756998_request_pd21mcms9_getActionArgs,
    displayName: 'sendmail',
    events: [inst_evh_1269646916756998_success_5db8uwfeg],
  });
  const inst_evh_1269646916756998_clickPreventStop_vvfeq58cn =
    new actionTypes.EventHandlerImpl({
      action: act_1269646916756998_request_pd21mcms9,
      event: 'click.prevent.stop',
      displayName: 'sendmail',
    });
  const inst_evh_1269646916756998_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_1269646916756998_clickPreventStop_6qgv5kmhe,
        inst_evh_1269646916756998_clickPreventStop_vvfeq58cn,
      ],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_7vqm22lt0,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_qzgkkduj2,
    inst_evh_7315092382398562_reload,
    inst_evh_1269646916756998_clickPreventStop_6qgv5kmhe,
    inst_evh_1269646916756998_success_5db8uwfeg,
    inst_evh_1269646916756998_clickPreventStop_vvfeq58cn,
    sendmail: inst_evh_1269646916756998_clickPreventStop_vvfeq58cn,
    inst_evh_1269646916756998_clickPreventStop,
  };

  const evh_7315092382398562_close_7vqm22lt0 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_7vqm22lt0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_qzgkkduj2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_qzgkkduj2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1269646916756998_clickPreventStop_6qgv5kmhe = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1269646916756998_clickPreventStop_6qgv5kmhe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1269646916756998_success_5db8uwfeg = async (event, scope) => {
    return await actions.inst_evh_1269646916756998_success_5db8uwfeg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1269646916756998_clickPreventStop_vvfeq58cn = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1269646916756998_clickPreventStop_vvfeq58cn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1269646916756998_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1269646916756998_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="GeneralSettings"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="h-100 overflow-auto px-3" v-if="loaded">
      <div class="card my-3">
        <div class="card-body">
          <h5>
            {{ pgettext('Settings', 'Email settings') }}
          </h5>
          <p class="card-text">
            {{
              gettext("Send a test email to the current user's email address.")
            }}
          </p>
          <ItpBox direction="column" alignItems="start">
            <ItpButton
              :text="pgettext('Tasks|Command', 'Send test email')"
              icon="mdi:cog"
              :spinning="actions.sendmail.isRunning"
              @click.prevent.stop="
                evh_1269646916756998_clickPreventStop($event, {})
              "
            >
            </ItpButton>
            <ItpAlert
              type="success"
              :show="sendmailResult === true"
              dismissible
              class="mt-3"
            >
              <span>
                {{ pgettext('Settings', 'Email has been successfully sent.') }}
              </span>
            </ItpAlert>
          </ItpBox>
        </div>
      </div>
    </div>
  </ItpPage>
</template>
